import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Globe, Megaphone, HelpCircle, Save, Send, AlertTriangle, Wrench, Power } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const COLOR_OPTIONS = [
  { value: "green", label: "Grün", cls: "bg-green-100 border-green-400 text-green-800" },
  { value: "blue", label: "Blau", cls: "bg-blue-100 border-blue-400 text-blue-800" },
  { value: "yellow", label: "Gelb", cls: "bg-yellow-100 border-yellow-400 text-yellow-800" },
  { value: "red", label: "Rot", cls: "bg-red-100 border-red-400 text-red-800" },
];

const TARGET_OPTIONS = [
  { value: "both", label: "Alle sehen den Banner" },
  { value: "admin", label: "Nur Wach-Admins" },
  { value: "employee", label: "Nur Mitarbeiter" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    ["clean"],
  ],
};

const GlobalContent = () => {
  const [loading, setLoading] = useState(true);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [savingHelp, setSavingHelp] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [showMaintenanceConfirm, setShowMaintenanceConfirm] = useState(false);
  const [kantinenCount, setKantinenCount] = useState(0);

  const [announcement, setAnnouncement] = useState({
    is_active: false,
    message: "",
    color: "blue",
    target: "both",
    version: 0,
    last_updated: null,
  });
  const [helpHtml, setHelpHtml] = useState("");
  const [helpMeta, setHelpMeta] = useState({ version: 0, last_updated: null });
  const [maintenance, setMaintenance] = useState({
    is_active: false,
    message: "",
    version: 0,
    last_updated: null,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [annRes, helpRes, wachenRes, maintRes] = await Promise.all([
        axios.get(`${API}/v1/admin/global/announcement`),
        axios.get(`${API}/v1/admin/global/help-content`),
        axios.get(`${API}/wachen`),
        axios.get(`${API}/v1/admin/global/maintenance`),
      ]);
      setAnnouncement({
        is_active: !!annRes.data.is_active,
        message: annRes.data.message || "",
        color: annRes.data.color || "blue",
        target: annRes.data.target || "both",
        version: annRes.data.version || 0,
        last_updated: annRes.data.last_updated || null,
      });
      setHelpHtml(helpRes.data.html_content || "");
      setHelpMeta({
        version: helpRes.data.version || 0,
        last_updated: helpRes.data.last_updated || null,
      });
      setMaintenance({
        is_active: !!maintRes.data.is_active,
        message: maintRes.data.message || "",
        version: maintRes.data.version || 0,
        last_updated: maintRes.data.last_updated || null,
      });
      const wachen = Array.isArray(wachenRes.data) ? wachenRes.data : [];
      setKantinenCount(wachen.filter((w) => w.callback_url && w.status === "active").length);
    } catch (error) {
      toast.error("Fehler beim Laden der globalen Inhalte");
    } finally {
      setLoading(false);
    }
  };

  const broadcastToast = (data) => {
    const b = data.broadcast || { total: 0, success: 0, failed: 0 };
    if (b.total === 0) {
      toast.success("Gespeichert. Keine Wache mit Callback-URL – Kantinen holen den Stand beim nächsten Sync (Pull).");
    } else if (b.failed === 0) {
      toast.success(`Gespeichert & sofort an alle ${b.success} Wache(n) gesendet.`);
    } else {
      toast.success(`Gespeichert. An ${b.success}/${b.total} Wachen gesendet. ${b.failed} offen (Pull-Fallback greift).`);
    }
  };

  const handleSaveAnnouncement = async () => {
    setSavingAnnouncement(true);
    try {
      const res = await axios.post(`${API}/v1/admin/global/announcement`, {
        is_active: announcement.is_active,
        message: announcement.message,
        color: announcement.color,
        target: announcement.target,
      });
      setAnnouncement((prev) => ({
        ...prev,
        version: res.data.version,
        last_updated: res.data.last_updated,
      }));
      broadcastToast(res.data);
    } catch (error) {
      toast.error("Fehler beim Speichern des Banners");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const handleSaveHelp = async () => {
    setSavingHelp(true);
    try {
      const res = await axios.post(`${API}/v1/admin/global/help-content`, {
        html_content: helpHtml,
      });
      setHelpHtml(res.data.html_content || "");
      setHelpMeta({ version: res.data.version, last_updated: res.data.last_updated });
      broadcastToast(res.data);
    } catch (error) {
      toast.error("Fehler beim Speichern der Hilfe-Inhalte");
    } finally {
      setSavingHelp(false);
    }
  };

  const handleSaveMaintenance = async () => {
    setShowMaintenanceConfirm(false);
    setSavingMaintenance(true);
    try {
      const res = await axios.post(`${API}/v1/admin/global/maintenance`, {
        is_active: maintenance.is_active,
        message: maintenance.message,
      });
      setMaintenance((prev) => ({
        ...prev,
        version: res.data.version,
        last_updated: res.data.last_updated,
      }));
      broadcastToast(res.data);
    } catch (error) {
      toast.error("Fehler beim Speichern des Wartungsmodus");
    } finally {
      setSavingMaintenance(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("de-DE");
    } catch {
      return dateStr;
    }
  };

  const activeColor = COLOR_OPTIONS.find((c) => c.value === announcement.color) || COLOR_OPTIONS[1];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="global-content-container">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)" }}>
          <Globe className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Globale Inhalte</h1>
          <p className="text-gray-600">Hinweise & Hilfe-Inhalte an alle Kantinen-Apps senden</p>
        </div>
      </div>

      {/* Reach info */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="kantinen-reach-info">
        <Send className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          Änderungen wirken sofort auf <strong data-testid="kantinen-count">{kantinenCount}</strong> Wache(n) mit hinterlegter Callback-URL.
          Wachen ohne Callback-URL holen den Stand automatisch beim nächsten Sync (alle 6 Std.).
        </div>
      </div>

      <Tabs defaultValue="banner" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="banner" data-testid="tab-banner">
            <Megaphone className="w-4 h-4 mr-2" />
            Wartungs-Banner
          </TabsTrigger>
          <TabsTrigger value="help" data-testid="tab-help">
            <HelpCircle className="w-4 h-4 mr-2" />
            Hilfe &amp; Neues
          </TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">
            <Wrench className="w-4 h-4 mr-2" />
            Wartungsmodus
          </TabsTrigger>
        </TabsList>

        {/* ===== BANNER TAB ===== */}
        <TabsContent value="banner">
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-blue-600" />
                Wartungs-Banner
              </CardTitle>
              <CardDescription className="text-gray-600">
                Wird ganz oben in jeder Kantinen-App angezeigt. Version {announcement.version} · Zuletzt aktualisiert: {formatDate(announcement.last_updated)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Active toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div>
                  <Label htmlFor="ann-active" className="text-gray-900 font-medium">Banner aktiv</Label>
                  <p className="text-sm text-gray-600">Wenn aktiv, sehen alle Kantinen den Hinweis.</p>
                </div>
                <Switch
                  id="ann-active"
                  checked={announcement.is_active}
                  onCheckedChange={(v) => setAnnouncement({ ...announcement, is_active: v })}
                  data-testid="announcement-active-switch"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="ann-message" className="text-gray-700">Nachricht</Label>
                <Textarea
                  id="ann-message"
                  value={announcement.message}
                  onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900 mt-1"
                  rows={3}
                  placeholder="z.B. Wartungsarbeiten heute von 22:00 - 23:00 Uhr"
                  data-testid="announcement-message-input"
                />
              </div>

              {/* Color */}
              <div>
                <Label className="text-gray-700 mb-2 block">Farbe</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setAnnouncement({ ...announcement, color: c.value })}
                      data-testid={`announcement-color-${c.value}`}
                      className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${c.cls} ${
                        announcement.color === c.value ? "ring-2 ring-offset-2 ring-gray-900 scale-105" : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Target audience */}
              <div>
                <Label className="text-gray-700 mb-2 block">Zielgruppe</Label>
                <Select
                  value={announcement.target}
                  onValueChange={(v) => setAnnouncement({ ...announcement, target: v })}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900 max-w-md" data-testid="announcement-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`announcement-target-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Legt fest, wer den Banner in der Kantine sieht (Alle / nur Wach-Admins / nur Mitarbeiter).
                </p>
              </div>

              {/* Preview */}
              <div>
                <Label className="text-gray-700 mb-2 block">Vorschau</Label>
                <div className={`flex items-center gap-2 p-4 rounded-lg border-2 ${activeColor.cls} ${!announcement.is_active ? "opacity-40" : ""}`} data-testid="announcement-preview">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-white/60">[Global]</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-white/60" data-testid="announcement-preview-target">
                    {(TARGET_OPTIONS.find((t) => t.value === announcement.target) || TARGET_OPTIONS[0]).label}
                  </span>
                  <span className="font-medium">{announcement.message || "Vorschau der Banner-Nachricht"}</span>
                </div>
              </div>

              <Button
                className="btn-primary"
                onClick={handleSaveAnnouncement}
                disabled={savingAnnouncement}
                data-testid="save-announcement-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingAnnouncement ? "Speichern..." : "Speichern & Senden"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== HELP TAB ===== */}
        <TabsContent value="help">
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                Hilfe &amp; Neues (global)
              </CardTitle>
              <CardDescription className="text-gray-600">
                Dieser Inhalt erscheint im Bereich „Hilfe &amp; Neues&quot; jeder Kantinen-App. Version {helpMeta.version} · Zuletzt aktualisiert: {formatDate(helpMeta.last_updated)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div data-testid="help-editor-wrapper" className="bg-white rounded-lg">
                <ReactQuill
                  theme="snow"
                  value={helpHtml}
                  onChange={setHelpHtml}
                  modules={QUILL_MODULES}
                  placeholder="Text für alle Kantinen eingeben..."
                />
              </div>

              <Button
                className="btn-primary"
                onClick={handleSaveHelp}
                disabled={savingHelp}
                data-testid="save-help-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingHelp ? "Speichern..." : "Speichern & Senden"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== MAINTENANCE TAB ===== */}
        <TabsContent value="maintenance">
          <Card className="card-white">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-red-600" />
                Wartungsmodus (global)
              </CardTitle>
              <CardDescription className="text-gray-600">
                Sperrt ALLE angeschlossenen Kantinen gleichzeitig (z.B. für Deployments/Störungen). Version {maintenance.version} · Zuletzt aktualisiert: {formatDate(maintenance.last_updated)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Big status toggle */}
              <div
                className={`flex items-center justify-between p-5 rounded-lg border-2 ${
                  maintenance.is_active ? "bg-red-50 border-red-400" : "bg-green-50 border-green-400"
                }`}
                data-testid="maintenance-status-box"
              >
                <div className="flex items-center gap-3">
                  <Power className={`w-7 h-7 ${maintenance.is_active ? "text-red-600" : "text-green-600"}`} />
                  <div>
                    <p className={`text-lg font-bold ${maintenance.is_active ? "text-red-700" : "text-green-700"}`}>
                      Wartungsmodus {maintenance.is_active ? "AKTIV" : "AUS"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {maintenance.is_active
                        ? "Alle Kantinen sind aktuell gesperrt."
                        : "Alle Kantinen sind normal erreichbar."}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={maintenance.is_active}
                  onCheckedChange={(v) => setMaintenance({ ...maintenance, is_active: v })}
                  data-testid="maintenance-active-switch"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="maint-message" className="text-gray-700">Nachricht (optional)</Label>
                <Textarea
                  id="maint-message"
                  value={maintenance.message}
                  onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900 mt-1"
                  rows={3}
                  placeholder="z.B. Sicherheitsupdate 22:00-22:30 Uhr"
                  data-testid="maintenance-message-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leer lassen → die Kantine zeigt ihren Standardtext an.
                </p>
              </div>

              <Button
                className={maintenance.is_active ? "bg-red-600 hover:bg-red-700 text-white" : "btn-primary"}
                onClick={() => setShowMaintenanceConfirm(true)}
                disabled={savingMaintenance}
                data-testid="save-maintenance-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingMaintenance ? "Speichern..." : "Speichern & Senden"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Maintenance confirmation dialog */}
      <Dialog open={showMaintenanceConfirm} onOpenChange={setShowMaintenanceConfirm}>
        <DialogContent className="bg-white" data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {maintenance.is_active ? "Wartungsmodus in ALLEN Kantinen einschalten?" : "Wartungsmodus in allen Kantinen ausschalten?"}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {maintenance.is_active
                ? `Dies sperrt sofort alle ${kantinenCount} Wache(n) mit Callback-URL. Nutzer sehen die Wartungsseite, bis du den Modus wieder deaktivierst.`
                : "Dies gibt alle Kantinen wieder frei."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMaintenanceConfirm(false)}
              className="border-gray-300 text-gray-700"
              data-testid="maintenance-confirm-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveMaintenance}
              className={maintenance.is_active ? "bg-red-600 hover:bg-red-700 text-white" : "btn-primary"}
              data-testid="maintenance-confirm-ok"
            >
              {maintenance.is_active ? "Ja, alle sperren" : "Ja, freigeben"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GlobalContent;
